import { ChevronLeft, ChevronRight, Lock, Mail, MessageCircle, Server, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { type AiSettingsSection } from "@/lib/context/sidebar-context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./ai-settings-panel.i18n";
import { ImapAccountTab } from "./imap-account";
import { McpServers } from "./mcp-servers";
import { Prompts } from "./prompts";
import { ToolPlayground } from "./tool-playground";

const sections: ReadonlyArray<{
  id: AiSettingsSection & keyof typeof T.en_GB.sections;
  icon: typeof MessageCircle;
}> = [
  { id: "prompts", icon: MessageCircle },
  { id: "playground", icon: Wrench },
  { id: "mcp", icon: Server },
  { id: "imap", icon: Mail },
];

interface AiSettingsPanelProps {
  clientId?: string | null;
  clientName?: string | null;
}

export function AiSettingsPanel({ clientId, clientName }: AiSettingsPanelProps) {
  const t = useT(T);
  const [activeSection, setActiveSection] = useState<AiSettingsSection | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: AiSettingsSection }>).detail;
      if (detail?.section) setActiveSection(detail.section);
    };
    window.addEventListener("navigate-to-ai-settings", handler);
    return () => window.removeEventListener("navigate-to-ai-settings", handler);
  }, []);

  const active = sections.find((s) => s.id === activeSection) ?? null;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 h-16 flex items-center gap-2">
        {active && (
          <button
            type="button"
            className="p-1 -ml-1 rounded-md hover:bg-muted text-muted-foreground"
            onClick={() => setActiveSection(null)}
            aria-label={t.back}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <h2 className="text-xl font-semibold">{active ? t.sections[active.id].label : t.title}</h2>
      </div>

      <div className="flex items-center gap-1.5 px-6 pb-2 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" />
        <span>{t.onlyVisibleToYou}</span>
      </div>

      <Separator />

      <ScrollArea className="flex-1">
        <div className="px-6 py-4">
          {active === null && (
            <ul className="divide-y divide-border -mx-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-2 py-3 hover:bg-muted/50 transition-colors text-left rounded-sm"
                      onClick={() => setActiveSection(section.id)}
                    >
                      <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{t.sections[section.id].label}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {t.sections[section.id].description}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {active?.id === "prompts" && <Prompts />}
          {active?.id === "playground" && (
            <ToolPlayground clientId={clientId} clientName={clientName} />
          )}
          {active?.id === "mcp" && <McpServers />}
          {active?.id === "imap" && <ImapAccountTab />}
        </div>
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
